import {
  Drawer,
  Button,
  Table,
  Row,
  Col,
  Space,
  Upload,
  Card,
  Tag,
  Alert,
  Typography,
} from "antd";
import {
  InboxOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Papa from "papaparse";
import { RootState } from "../../../shared/types/common";
import { showNotification } from "../../../shared/utils/utils";
import { Client } from "../../../core/api-client";
import { API_GET_ALL_DEPARTMENT_LIST } from "../../../shared/routes/private.routes";
import { bulkUploadJobDescriptions } from "../../../core/store/reducers/candidate-screening.slice";

interface Props {
  onClose: () => void;
  visible: boolean;
  onSubmit?: () => void;
}

// CSV schema shared with the backend (candidate-screening.service.ts).
const TEMPLATE_HEADERS = [
  "title",
  "department",
  "status",
  "similarityWeight",
  "ruleWeight",
  "aiWeight",
  "description",
];
const REQUIRED_HEADERS = ["title", "description"];
const MAX_ROWS = 500;

interface PreviewRow {
  key: number;
  row: number; // spreadsheet line (header = row 1)
  title: string;
  valid: boolean;
  errors: string[];
}

// Lower-case every key so header matching is case-insensitive (mirrors BE).
const normalizeKeys = (obj: Record<string, any>): Record<string, string> => {
  const out: Record<string, string> = {};
  Object.keys(obj || {}).forEach((k) => {
    out[k.trim().toLowerCase()] = `${obj[k] ?? ""}`.trim();
  });
  return out;
};

const BulkUploadJobDescriptionsComponent = ({
  onClose,
  visible,
  onSubmit = () => {
    return;
  },
}: Props) => {
  const dispatch = useDispatch();
  const { screenType } = useSelector((state: RootState) => state.settings);

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [validCount, setValidCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [serverResult, setServerResult] = useState<any>(null);
  const [deptNames, setDeptNames] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Load department names so unknown departments are flagged client-side too.
    Client.get(API_GET_ALL_DEPARTMENT_LIST)
      .then((res: any) => {
        const list = res?.data?.data || [];
        setDeptNames(
          new Set(
            list
              .map((d: any) => `${d?.departmentName ?? ""}`.trim().toLowerCase())
              .filter(Boolean)
          )
        );
      })
      .catch(() => setDeptNames(new Set()));
  }, []);

  const validateRow = (v: Record<string, string>): string[] => {
    const errors: string[] = [];
    const title = (v["title"] || "").trim();
    const description = (v["description"] || "").trim();
    const statusRaw = (v["status"] || "").trim();
    const dept = (v["department"] || "").trim();

    if (!title) errors.push("Title is required.");
    else if (title.length > 255) errors.push("Title exceeds 255 characters.");
    if (!description) errors.push("Description is required.");

    if (statusRaw) {
      const s = statusRaw.toUpperCase();
      if (s !== "ACTIVE" && s !== "INACTIVE")
        errors.push(`Invalid status "${statusRaw}" (use ACTIVE or INACTIVE).`);
    }

    // Only validate department names if the list actually loaded.
    if (dept && deptNames.size > 0 && !deptNames.has(dept.toLowerCase())) {
      errors.push(`Unknown department "${dept}".`);
    }

    const rawSim = (v["similarityweight"] || "").trim();
    const rawRule = (v["ruleweight"] || "").trim();
    const rawAi = (v["aiweight"] || "").trim();
    if (rawSim !== "" || rawRule !== "" || rawAi !== "") {
      const sim = Number(rawSim);
      const rule = Number(rawRule);
      const ai = Number(rawAi);
      const invalid = [
        [rawSim, sim],
        [rawRule, rule],
        [rawAi, ai],
      ].some(
        ([raw, num]) =>
          raw === "" ||
          !Number.isFinite(num as number) ||
          (num as number) < 0 ||
          (num as number) > 1
      );
      if (invalid) {
        errors.push(
          "Weights (similarityWeight, ruleWeight, aiWeight) must all be numbers between 0 and 1."
        );
      } else if (Math.abs(sim + rule + ai - 1) > 0.01) {
        errors.push(
          `Weights must sum to 1.00 (got ${(sim + rule + ai).toFixed(2)}).`
        );
      }
    }

    return errors;
  };

  const parseAndPreview = (selected: File) => {
    setServerResult(null);
    setFileError("");
    setPreviewRows([]);
    setValidCount(0);

    Papa.parse(selected, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (h) => h.trim(),
      complete: (result: any) => {
        const fields: string[] = (result?.meta?.fields || []).map((f: string) =>
          f.trim().toLowerCase()
        );
        const missing = REQUIRED_HEADERS.filter((h) => !fields.includes(h));
        if (missing.length) {
          setFileError(
            `CSV is missing required column(s): ${missing.join(
              ", "
            )}. Expected header row: ${TEMPLATE_HEADERS.join(", ")}.`
          );
          setFile(selected);
          return;
        }

        const dataRows = (result?.data || []).filter(
          (r: any) =>
            r &&
            Object.values(r).some((val: any) => `${val ?? ""}`.trim() !== "")
        );
        if (!dataRows.length) {
          setFileError("CSV has no data rows.");
          setFile(selected);
          return;
        }
        if (dataRows.length > MAX_ROWS) {
          setFileError(
            `Too many rows (${dataRows.length}). Max ${MAX_ROWS} per upload.`
          );
          setFile(selected);
          return;
        }

        let valid = 0;
        const rows: PreviewRow[] = dataRows.map((raw: any, idx: number) => {
          const v = normalizeKeys(raw);
          const errors = validateRow(v);
          if (!errors.length) valid++;
          return {
            key: idx,
            row: idx + 2, // header is spreadsheet row 1
            title: v["title"] || "",
            valid: errors.length === 0,
            errors,
          };
        });
        setPreviewRows(rows);
        setValidCount(valid);
        setFile(selected);
      },
      error: () => {
        setFileError("Could not read the CSV file. Please check the format.");
        setFile(selected);
      },
    });
  };

  const handleAddFile = (info: any) => {
    const selected: File = info.file;
    if (!selected) return;
    const okType =
      selected.type === "text/csv" ||
      selected.type === "application/vnd.ms-excel" ||
      /\.csv$/i.test(selected.name);
    if (!okType) {
      showNotification("Only .csv files are allowed.", "error");
      return;
    }
    parseAndPreview(selected);
  };

  const handleDownloadTemplate = () => {
    const sample = [
      TEMPLATE_HEADERS.join(","),
      [
        "Senior Backend Engineer",
        "",
        "ACTIVE",
        "0.5",
        "0.2",
        "0.3",
        '"We need a NestJS/TypeScript engineer with strong MySQL and REST API experience. 5+ years backend."',
      ].join(","),
      [
        "Frontend Engineer",
        "",
        "ACTIVE",
        "0.5",
        "0.2",
        "0.3",
        '"React 18 + TypeScript + Redux Toolkit. Build responsive UIs and integrate REST APIs."',
      ].join(","),
    ].join("\r\n");
    const blob = new Blob(["﻿" + sample], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "job-descriptions-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    if (!file) {
      showNotification("Please choose a CSV file.", "warning");
      return;
    }
    if (fileError) {
      showNotification(fileError, "error");
      return;
    }
    if (validCount === 0) {
      showNotification(
        "No valid rows to import. Fix the highlighted errors and re-upload.",
        "warning"
      );
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append("file", file);
    dispatch(bulkUploadJobDescriptions(formData)).then((val: any) => {
      setSubmitting(false);
      const payload = val?.payload;
      // Structural rejection from the backend (400) — surface its message.
      if (payload?.status === 400) {
        const msg =
          payload?.data?.message || "The file was rejected by the server.";
        setFileError(msg);
        showNotification(msg, "error");
        return;
      }
      const data = payload?.data?.data;
      if (data) {
        setServerResult(data);
        if (data.created > 0) {
          showNotification(payload?.data?.message, "success");
          onSubmit(); // refresh the JD list behind the drawer
        } else {
          showNotification(
            payload?.data?.message || "No rows were imported.",
            "warning"
          );
        }
      }
    });
  };

  const resetFile = () => {
    setFile(null);
    setFileError("");
    setPreviewRows([]);
    setValidCount(0);
    setServerResult(null);
  };

  const previewColumns = [
    { title: "Row", dataIndex: "row", key: "row", width: 70 },
    { title: "Title", dataIndex: "title", key: "title" },
    {
      title: "Status",
      key: "valid",
      width: 100,
      render: (_: any, r: PreviewRow) =>
        r.valid ? (
          <Tag color="green">Valid</Tag>
        ) : (
          <Tag color="red">Invalid</Tag>
        ),
    },
    {
      title: "Issues",
      key: "errors",
      render: (_: any, r: PreviewRow) =>
        r.errors.length ? (
          <span style={{ color: "#cf1322", fontSize: 12 }}>
            {r.errors.join(" ")}
          </span>
        ) : (
          <span style={{ color: "#52c41a" }}>—</span>
        ),
    },
  ];

  const resultColumns = [
    { title: "Row", dataIndex: "row", key: "row", width: 70 },
    { title: "Title", dataIndex: "title", key: "title" },
    {
      title: "Result",
      key: "status",
      width: 110,
      render: (_: any, r: any) =>
        r.status === "created" ? (
          <span>
            <CheckCircleTwoTone twoToneColor="#52c41a" /> Created
          </span>
        ) : (
          <span>
            <CloseCircleTwoTone twoToneColor="#cf1322" /> Failed
          </span>
        ),
    },
    {
      title: "Issues",
      key: "errors",
      render: (_: any, r: any) =>
        r.errors?.length ? (
          <span style={{ color: "#cf1322", fontSize: 12 }}>
            {r.errors.join(" ")}
          </span>
        ) : (
          <span style={{ color: "#8c8c8c" }}>—</span>
        ),
    },
  ];

  return (
    <Drawer
      maskClosable={false}
      title={<strong>Bulk Upload Job Descriptions (CSV)</strong>}
      className="form-drawer"
      width={
        screenType.isPortrait ? "95%" : screenType.isLg || screenType.isXl ? 900 : 640
      }
      onClose={onClose}
      open={visible}
    >
      <Card
        style={{ borderRadius: 8, backgroundColor: "#e6f7ff", marginBottom: 16 }}
      >
        <p style={{ fontSize: 13, marginBottom: 4 }}>
          <ExclamationCircleOutlined /> Upload a <strong>.csv</strong> with one
          job description per row. Required columns:{" "}
          <strong>title</strong> and <strong>description</strong>.
        </p>
        <p style={{ fontSize: 12, marginBottom: 8 }}>
          Optional columns: department (name), status (ACTIVE/INACTIVE),
          similarityWeight, ruleWeight, aiWeight (must sum to 1.00 if any is
          set; otherwise 0.5 / 0.2 / 0.3 defaults apply).
        </p>
        <Button
          size="small"
          icon={<DownloadOutlined />}
          onClick={handleDownloadTemplate}
        >
          Download CSV template
        </Button>
      </Card>

      {!serverResult && (
        <Upload.Dragger
          name="file"
          multiple={false}
          showUploadList={false}
          customRequest={handleAddFile}
          accept=".csv"
          style={{ padding: "16px 0" }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag a .csv file here</p>
          {file && (
            <p className="ant-upload-hint">
              Selected: <strong>{file.name}</strong>
            </p>
          )}
        </Upload.Dragger>
      )}

      {fileError && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message="File cannot be imported"
          description={fileError}
        />
      )}

      {/* Client-side preview (before import) */}
      {!serverResult && previewRows.length > 0 && !fileError && (
        <>
          <div style={{ margin: "16px 0 8px" }}>
            <Typography.Text strong>
              {validCount} valid
            </Typography.Text>{" "}
            /{" "}
            <Typography.Text type={validCount < previewRows.length ? "danger" : undefined}>
              {previewRows.length - validCount} invalid
            </Typography.Text>{" "}
            of {previewRows.length} row(s). Only valid rows will be imported.
          </div>
          <Table
            rowKey="key"
            size="small"
            columns={previewColumns}
            dataSource={previewRows}
            pagination={{ pageSize: 8, hideOnSinglePage: true }}
          />
        </>
      )}

      {/* Server result (after import) */}
      {serverResult && (
        <>
          <Alert
            type={serverResult.created > 0 ? "success" : "warning"}
            showIcon
            style={{ margin: "16px 0" }}
            message={`Imported ${serverResult.created} of ${serverResult.total} job description(s)`}
            description={
              serverResult.failed > 0
                ? `${serverResult.failed} row(s) failed — see the table below.`
                : "All rows imported successfully."
            }
          />
          <Table
            rowKey="row"
            size="small"
            columns={resultColumns}
            dataSource={serverResult.results || []}
            pagination={{ pageSize: 8, hideOnSinglePage: true }}
          />
        </>
      )}

      <Row justify="end" style={{ marginTop: 16 }}>
        <Col>
          <Space>
            {serverResult ? (
              <>
                <Button onClick={resetFile}>Upload another</Button>
                <Button type="primary" onClick={onClose}>
                  Done
                </Button>
              </>
            ) : (
              <>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                  type="primary"
                  loading={submitting}
                  onClick={handleImport}
                  disabled={!file || !!fileError || validCount === 0}
                >
                  Import {validCount > 0 ? `${validCount} ` : ""}row
                  {validCount === 1 ? "" : "s"}
                </Button>
              </>
            )}
          </Space>
        </Col>
      </Row>
    </Drawer>
  );
};

export default BulkUploadJobDescriptionsComponent;
